import { Conditions, Fields, Operands, RuleCreationSource } from '@constants'
import {
  BlueprintBalanceEntry,
  createAccountingRule,
  fetchBlueprintBalance,
  type HttpError,
  IAccountingRule,
  IAccountingRuleBlueprintEntry,
  updateAccountingRule
} from '@query'
import { EMPTY_BLUEPRINT_ENTRY } from '@root/pages/SettingsPage/sections/AutomationSettings/AccountingRule/Form/AccountingRuleForm'
import type { ReactFlowJsonObject } from '@xyflow/react'
import type { UseFormSetError } from 'react-hook-form'

export type CreateDocumentRuleFieldSource = 'incoming' | 'own'

export interface CreateDocumentRuleFormValues {
  description: string
  descriptionSource: CreateDocumentRuleFieldSource
  contactSource: CreateDocumentRuleFieldSource
  contactId: number | null
  blueprint: IAccountingRule['blueprint']
  attachment_exempt: boolean
}

export interface PendingCreateDocumentRule {
  nodeId: string
  ruleId: number | null
  values: CreateDocumentRuleFormValues
  showErrors?: boolean
}

export interface CreateDocumentRuleNodePatch {
  accounting_rule_id: number
}

export interface CreateDocumentRuleSession {
  nodeId: string
  getDraft: () => PendingCreateDocumentRule
  /** Rule fields dirty — used when collecting drafts for workflow save. */
  isDirty: () => boolean
  /** Full form dirty (node + rule) — drives the inspector unsaved bar. */
  isFormDirty: boolean
  onRevert: () => void
  onSaveBlocked: (error: CreateDocumentRuleSaveError) => void
  onPersisted: () => void
}

export class CreateDocumentRuleSaveError extends Error {
  readonly nodeId: string
  readonly httpError?: HttpError

  constructor(nodeId: string, httpError?: HttpError) {
    super('CREATE_DOCUMENT_RULE_SAVE')
    this.name = 'CreateDocumentRuleSaveError'
    this.nodeId = nodeId
    this.httpError = httpError
  }
}

export const isCreateDocumentRuleSaveError = (
  error: unknown
): error is CreateDocumentRuleSaveError => error instanceof CreateDocumentRuleSaveError

export const defaultCreateDocumentRuleValues = (): CreateDocumentRuleFormValues => ({
  description: '',
  descriptionSource: 'incoming',
  contactSource: 'incoming',
  contactId: null,
  blueprint: {
    debet_entries: [{ ...EMPTY_BLUEPRINT_ENTRY }],
    credit_entries: [{ ...EMPTY_BLUEPRINT_ENTRY }]
  },
  attachment_exempt: false
})

export const valuesFromAccountingRule = (rule: IAccountingRule): CreateDocumentRuleFormValues => {
  const contactRule = (rule.rule_node?.rules ?? []).find(
    item => 'field' in item && item.field === Fields.CONTACT
  )
  const contactIds =
    contactRule && 'value' in contactRule && Array.isArray(contactRule.value)
      ? (contactRule.value as number[])
      : []
  const description = rule.description?.trim() ?? ''
  return {
    description,
    descriptionSource: description ? 'own' : 'incoming',
    contactSource: contactIds.length > 0 ? 'own' : 'incoming',
    contactId: contactIds[0] ?? null,
    blueprint: {
      debet_entries: rule.blueprint?.debet_entries?.length
        ? rule.blueprint.debet_entries
        : [{ ...EMPTY_BLUEPRINT_ENTRY }],
      credit_entries: rule.blueprint?.credit_entries?.length
        ? rule.blueprint.credit_entries
        : [{ ...EMPTY_BLUEPRINT_ENTRY }]
    },
    attachment_exempt: Boolean(rule.attachment_exempt)
  }
}

export const snapshotBlueprintEntries = (
  entries: IAccountingRuleBlueprintEntry[] | undefined
): BlueprintBalanceEntry[] =>
  (entries ?? []).map(entry => ({
    account_id: entry.account_id ?? null,
    amount: String(entry.amount ?? '').trim()
  }))

export const isCreateDocumentRuleEntriesReady = (values: CreateDocumentRuleFormValues): boolean => {
  const debitEntries = snapshotBlueprintEntries(values.blueprint?.debet_entries)
  const creditEntries = snapshotBlueprintEntries(values.blueprint?.credit_entries)
  return (
    debitEntries.length > 0 &&
    creditEntries.length > 0 &&
    debitEntries.every(entry => entry.account_id != null && entry.amount.length > 0) &&
    creditEntries.every(entry => entry.account_id != null && entry.amount.length > 0)
  )
}

export const isCreateDocumentRuleComplete = (values: CreateDocumentRuleFormValues): boolean => {
  const descriptionOk = values.descriptionSource !== 'own' || Boolean(values.description?.trim())
  const contactOk = values.contactSource !== 'own' || typeof values.contactId === 'number'
  return descriptionOk && contactOk && isCreateDocumentRuleEntriesReady(values)
}

export const applyCreateDocumentRuleClientErrors = (
  values: CreateDocumentRuleFormValues,
  setError: UseFormSetError<CreateDocumentRuleFormValues>,
  requiredMessage: string
): void => {
  if (values.descriptionSource === 'own' && !values.description.trim()) {
    setError('description', { type: 'validate', message: requiredMessage })
  }
  if (values.contactSource === 'own' && typeof values.contactId !== 'number') {
    setError('contactId', { type: 'validate', message: requiredMessage })
  }
}

const stripVatFromEntries = (
  entries: IAccountingRuleBlueprintEntry[] | undefined
): IAccountingRuleBlueprintEntry[] =>
  (entries ?? []).map(entry => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { default_vat_code, default_vat_rate_label, ...rest } = entry
    return rest
  })

export const saveCreateDocumentRule = async ({
  slug,
  workflowId,
  nodeId,
  ruleId,
  values,
  vatObliged,
  name
}: {
  slug: string
  workflowId: number
  nodeId: string
  ruleId: number | null
  values: CreateDocumentRuleFormValues
  vatObliged: boolean
  name: string
}): Promise<IAccountingRule> => {
  const payload: Partial<IAccountingRule> = {
    name,
    enabled: true,
    created_from: RuleCreationSource.WORKFLOW,
    wait_for_attachment: false,
    attachment_exempt: Boolean(values.attachment_exempt),
    workflow: workflowId,
    workflow_node_uuid: nodeId,
    payment_account: null,
    description: values.descriptionSource === 'own' ? values.description.trim() : '',
    blueprint: values.blueprint,
    rule_node: {
      operand: Operands.AND,
      rules:
        values.contactSource === 'own' && typeof values.contactId === 'number'
          ? [
              {
                field: Fields.CONTACT,
                condition: Conditions.CONTACT_IS_IN,
                value: [values.contactId]
              }
            ]
          : []
    }
  }
  if (!vatObliged && payload.blueprint) {
    payload.blueprint = {
      ...payload.blueprint,
      debet_entries: stripVatFromEntries(payload.blueprint.debet_entries),
      credit_entries: stripVatFromEntries(payload.blueprint.credit_entries)
    }
  }
  if (ruleId != null) {
    return updateAccountingRule({ slug, id: ruleId, skipMatching: true }, payload)
  }
  return createAccountingRule({ slug, skipMatching: true }, payload)
}

export const fetchCreateDocumentRuleBalanceOk = async (
  slug: string,
  values: CreateDocumentRuleFormValues
): Promise<boolean> => {
  if (!isCreateDocumentRuleEntriesReady(values)) return false
  try {
    const result = await fetchBlueprintBalance({
      slug,
      debetEntries: snapshotBlueprintEntries(values.blueprint?.debet_entries),
      creditEntries: snapshotBlueprintEntries(values.blueprint?.credit_entries)
    })
    return result.balanced === true
  } catch {
    return false
  }
}

export const createDocumentNodePatchFromRule = (
  saved: IAccountingRule
): CreateDocumentRuleNodePatch => ({
  accounting_rule_id: saved.id
})

export const patchCreateDocumentNodeInFlow = (
  reactFlowData: ReactFlowJsonObject,
  nodeId: string,
  patch: CreateDocumentRuleNodePatch
): ReactFlowJsonObject => ({
  ...reactFlowData,
  nodes: (reactFlowData.nodes ?? []).map(node => {
    if (node.id !== nodeId) return node
    const data = (node.data ?? {}) as { config?: Record<string, unknown> }
    return {
      ...node,
      data: {
        ...data,
        config: {
          ...(data.config ?? {}),
          ...patch
        }
      }
    }
  })
})
